import type { Firestore } from 'firebase/firestore/lite';
import {
    db,
    dbQuestionBank,
    dbQuestionBankProd,
    dbQuestionBankProdv2,
    dbQuestionBankTestdata,
    isQuestionBankProdFirestoreConfigured,
    isQuestionBankProdv2FirestoreConfigured,
} from '../firebase/client';
import {
    FIREBASE_STORAGE_BUCKET_QA,
    FIREBASE_STORAGE_BUCKET_TESTDATA,
} from '../firebase/config';
import type { QuestionsDatabaseId } from '../Pages/DataBaseV3/Questions/questionsDatabaseId';
import { CATALOG_MIRROR_IMAGE_ROOT } from './catalogStaticManifest.v1';
import { catalogImageForTemplateKey } from './testGroupAndTestConfig.v1';

/**
 * Per-state TestGroup collection (one doc per group; tests nested in `tests[]`).
 * Example: `testgroup_test_nj`, `testgroup_test_tx` (matches `questions_tx` pattern).
 */
export function getStateTestGroupsCollectionName(stateCode: string): string {
    const state = String(stateCode || '')
        .trim()
        .toLowerCase();
    const prefix =
        process.env.REACT_APP_TEST_CATALOG_STATE_COLLECTION_PREFIX?.trim() || 'testgroup_test';
    return `${prefix}_${state}`;
}

/** @deprecated Legacy flat layout — read-only fallback during migration. */
export function getTestCatalogGroupsCollection(): string {
    return process.env.REACT_APP_TEST_CATALOG_GROUPS_COLLECTION ?? 'test_catalog_groups';
}

/** @deprecated Legacy flat layout — read-only fallback during migration. */
export function getTestCatalogTestsCollection(): string {
    return process.env.REACT_APP_TEST_CATALOG_TESTS_COLLECTION ?? 'test_catalog_tests';
}

/**
 * Storage prefix for catalog covers (`images/catalog/`). BY_DIFFICULTIES and BY_TOPICS use
 * different files per templateKey — see docs/docsForUI/CATALOG_STATIC_IMAGES_V1.md.
 * @see getTestCatalogImageStoragePath in testCatalogStorage.ts
 */
export { CATALOG_MIRROR_IMAGE_ROOT };

/** @deprecated Both kinds use {@link CATALOG_MIRROR_IMAGE_ROOT}. */
export const TEST_CATALOG_IMAGE_ROOT = {
    testGroup: CATALOG_MIRROR_IMAGE_ROOT,
    test: CATALOG_MIRROR_IMAGE_ROOT,
} as const;

export type TestCatalogImageKind = keyof typeof TEST_CATALOG_IMAGE_ROOT;

/**
 * Single catalog cover image size (5:4) — stored under {@link CATALOG_MIRROR_IMAGE_ROOT}.
 * One JPEG derivative only; web, iOS, and Android read `imageUrl`.
 */
export const TEST_CATALOG_IMAGE_SPEC = { width: 800, height: 640 } as const;

/** Human-readable aspect ratio for admin upload hints. */
export const TEST_CATALOG_IMAGE_ASPECT_LABEL = '5:4';

/** Lines shown under cover image upload in Test Catalog group/test modals. */
export function getTestCatalogCoverImageUploadHint(
    entityLabel: 'test group' | 'test',
    storageBucketId: string
): readonly string[] {
    const { width, height } = TEST_CATALOG_IMAGE_SPEC;
    return [
        `Uploads to Storage bucket “${storageBucketId}” under ${CATALOG_MIRROR_IMAGE_ROOT}/ (static config per templateKey; see CATALOG_STATIC_IMAGES_V1.md).`,
        `Recommended: ${width}×${height} px (${TEST_CATALOG_IMAGE_ASPECT_LABEL} landscape), JPEG or PNG.`,
        `Optional source: ${width * 2}×${height * 2} px or larger for sharper downscale; admin saves one JPEG.`,
        `One cover per ${entityLabel}; same imageUrl on web, iOS, and Android (each app crops to fit).`,
        'Center the main subject; avoid text or logos near the edges.',
    ];
}

/** @deprecated Legacy multi-derivative preview tree under `test-catalog/`. */
export const TEST_CATALOG_STORAGE_PREFIX = 'test-catalog';

/** @deprecated Replaced by {@link TEST_CATALOG_IMAGE_SPEC}. */
export const PREVIEW_IMAGE_SPECS = {
    full: { width: 1280, height: 720 },
    medium: { width: 640, height: 360 },
    thumb: { width: 320, height: 180 },
} as const;

/** Default tests-per-group target used at seed time. */
export const DEFAULT_TARGET_QUESTIONS_PER_TEST = 30;

/**
 * Canonical Storage object path for catalog config images (state-independent).
 * Upload JPEG assets to this path in the shared content bucket before seeding.
 */
/** @deprecated Use {@link catalogImageForTemplateKey} from testGroupAndTestConfig.v1 */
export function catalogConfigImageStoragePath(
    _kind: TestCatalogImageKind,
    templateKey: string
): string {
    const image = catalogImageForTemplateKey(templateKey);
    if (!image?.imageStoragePath) {
        throw new Error(`No imageStoragePath in config for templateKey ${templateKey}`);
    }
    return image.imageStoragePath;
}

/** @deprecated Use {@link catalogImageForTemplateKey} from testGroupAndTestConfig.v1 */
export function catalogConfigImageUrl(_kind: TestCatalogImageKind, templateKey: string): string {
    const image = catalogImageForTemplateKey(templateKey);
    if (!image?.imageUrl) {
        throw new Error(`No imageUrl in config for templateKey ${templateKey}`);
    }
    return image.imageUrl;
}

/**
 * Resolve the Firestore client for the selected database id. Mirrors
 * `bankFirestore` from `questionBankFirestore.ts` so the Test Catalog and Question Bank
 * always agree on which database they're addressing.
 *
 * Throws when the caller asks for a database that isn't configured at build time
 * (e.g. `vehiwheelprod` without `REACT_APP_FIRESTORE_DB_PROD`).
 */
export function getTestCatalogFirestore(databaseId: QuestionsDatabaseId): Firestore {
    if (databaseId === 'vehiwheelqa') return dbQuestionBank;
    if (databaseId === 'vehiwheeltestdata') return dbQuestionBankTestdata;
    if (databaseId === 'vehiwheelprod') {
        if (!isQuestionBankProdFirestoreConfigured || !dbQuestionBankProd) {
            throw new Error(
                'Production Test Catalog Firestore is not configured (set REACT_APP_FIRESTORE_DB_PROD).'
            );
        }
        return dbQuestionBankProd;
    }
    if (databaseId === 'vehiwheelprodv2') {
        if (!isQuestionBankProdv2FirestoreConfigured || !dbQuestionBankProdv2) {
            throw new Error(
                'Prod v2 Test Catalog Firestore is not configured (set REACT_APP_FIRESTORE_DB_PRODV2).'
            );
        }
        return dbQuestionBankProdv2;
    }
    return db;
}

/** Which DBs the user may pick from for Test Catalog operations. Same set as Question Bank. */
export function getAvailableTestCatalogDatabaseIds(): QuestionsDatabaseId[] {
    const out: QuestionsDatabaseId[] = ['default', 'vehiwheelqa', 'vehiwheeltestdata'];
    if (isQuestionBankProdFirestoreConfigured) out.push('vehiwheelprod');
    if (isQuestionBankProdv2FirestoreConfigured) out.push('vehiwheelprodv2');
    return out;
}
